import AbstractApiModel from '@/models/abstracts/AbstractApiModel';
import doc from '@model-core/decorators/doc';
import MemberModel from '@/models/entities/MemberModel';
import PeriodModel from '@/models/entities/PeriodModel';
import TeamMemberModel from '@/models/entities/TeamMemberModel';
import TimeSheetSummaryModel from '@/models/entities/TimeSheetSummaryModel';
import { TIME_SHEET_STATUS, TIME_SHEET_TYPE } from '@/models/references/references';
import { referenceOf } from '@/stores/useReferenceOptions';
import { urnOf } from '@/models/urn';
import dayjs from '@/lib/dayjs';
import type { Dayjs } from 'dayjs';

/** URN pour écrire une relation vers cette ressource (corps d'un POST / PATCH) ; un filtre prend l'id */
export const timeSheetUrn = (timeSheetId: id | number) => urnOf('time_sheet', timeSheetId);

/**
 * Feuille d'heures d'un employé (Member) pour une période (GET /time_sheets ; une par employé et par période).
 * Créée en brouillon par l'employé au premier besoin (POST {member, period} : première ligne, ou soumission sans ligne),
 * soumise en la signant. teamMember : l'appartenance du déclarant à une équipe, suivie tant que la feuille est en
 * brouillon (gardée, inactive, s'il sort de son équipe : « n'est plus dans l'équipe »), figée à la soumission. Absence,
 * par le Dirigeant ou le manager de l'équipe : POST {member, period, timeSheetType, teamMember} sans feuille, PATCH
 * {timeSheetType} sur un brouillon.
 * timeSheetStatus arrive avec son id. totalMinutes : somme de ses lignes, lue dans son résumé (TimeSheetSummary, ressource
 * à part : fetchSummaries(sheets, TIME_SHEET_SUMMARIES)), 0 tant qu'il n'est pas chargé.
 * member (puis son user), teamMember (puis son équipe) et period réduits à leur identité : à résoudre par l'écran
 * (fetchResolvable('member', 'user'), fetchResolvable('teamMember', 'team'), fetchResolvable('period')). L'employé ne
 * lit que ses feuilles, le Dirigeant celles des sociétés qu'il dirige, le manager celles de ses équipes, l'admin toutes.
 * Filtres : member, period, teamMember.team, timeSheetStatus (ou liste) ; tri member.user.lastName / firstName,
 * teamMember.team.name.
 */
export default class TimeSheetModel extends AbstractApiModel {
  protected _path = '/time_sheets/{?id}';

  @doc.model(() => MemberModel) declare member: MemberModel;
  @doc.model(() => TeamMemberModel) declare teamMember: TeamMemberModel;
  @doc.model(() => PeriodModel) declare period: PeriodModel;

  declare _tsFilters: {
    member?: string;
    period?: string;
    'period.company'?: string;
    'teamMember.team'?: string;
    search?: string;
    timeSheetStatus?: id | id[];
  };
  declare _tsSorts: {
    'member.user.lastName'?: unknown;
    'member.user.firstName'?: unknown;
    'teamMember.team.name'?: unknown;
    submittedAt?: unknown;
    'timeSheetSummaries.totalMinutes'?: unknown;
  };

  /** Rempli par fetchSummaries (le résumé n'arrive pas avec la feuille) */
  @doc.model(() => TimeSheetSummaryModel) declare timeSheetSummary: TimeSheetSummaryModel;

  get urn(): string {
    return timeSheetUrn(this.id);
  }

  /**
   * Soumise le : signature de l'employé ou, pour une absence, sa déclaration par le manager ou le Dirigeant ; null en
   * brouillon (vidé après un refus)
   */
  get submittedAt(): Dayjs | null {
    const value = this.get<string | null, null>('submittedAt', null);
    return value ? dayjs(value) : null;
  }

  get totalMinutes(): number {
    return this.timeSheetSummary.totalMinutes;
  }

  /** DRAFT, SUBMITTED, PENDING_MANAGER… */
  get timeSheetStatusReference(): string {
    return referenceOf('timeSheetStatuses', this.get<unknown, unknown>('timeSheetStatus', null))?.reference ?? '';
  }

  get timeSheetStatusLabel(): string {
    return referenceOf('timeSheetStatuses', this.get<unknown, unknown>('timeSheetStatus', null))?.label ?? '';
  }

  /** Absence : créée par le Dirigeant pour un employé qui n'a pas déclaré (sans ligne, directement en validation) */
  get isAbsence(): boolean {
    return (
      referenceOf('timeSheetTypes', this.get<unknown, unknown>('timeSheetType', null))?.reference ===
      TIME_SHEET_TYPE.ABSENCE
    );
  }

  /** En attente du manager ou du Dirigeant : à valider ou refuser */
  get isPending(): boolean {
    return (
      this.timeSheetStatusReference === TIME_SHEET_STATUS.PENDING_MANAGER ||
      this.timeSheetStatusReference === TIME_SHEET_STATUS.PENDING_DIRECTOR
    );
  }

  /** Seul un brouillon se modifie (lignes) et se soumet */
  get isDraft(): boolean {
    return this.timeSheetStatusReference === TIME_SHEET_STATUS.DRAFT;
  }

  /** Id du Member (déclarant) : fiche employé /employees/{id} */
  get memberId(): number {
    return Number(this.member.id) || 0;
  }

  get firstName(): string {
    return this.member.firstName;
  }

  get lastName(): string {
    return this.member.lastName;
  }

  /** NOM Prénom : convention du projet */
  get fullName(): string {
    return `${this.lastName} ${this.firstName}`.trim();
  }

  /** Équipe de son appartenance (figée à la soumission) */
  get teamName(): string {
    return this.teamMember.teamName;
  }

  get teamColor(): string {
    return this.teamMember.teamColor;
  }

  get teamId(): number {
    return this.teamMember.teamId;
  }
}
